import React, { useEffect, useState } from "react";
import { TextField, CircularProgress } from "@material-ui/core";
import {
	BookingContentName,
	BookingContentWrap,
	ButtonUserWrap,
	MyBookingWrapper,
	AdminTransactionAddButtton,
} from "../MyBooking/styled/DesStyled";
import {
	PaymentCycleConfig,
	getPaymentCycleConfig,
	savePaymentCycleConfig,
} from "../../../services/PaymentCycleConfigService";
import PermissionGate from "../../controls/auth/PermissionGate";

const FIELDS: { key: keyof PaymentCycleConfig; label: string; description: string }[] = [
	{
		key: "cycle1EndDay",
		label: "Ngày kết thúc Kỳ 1",
		description: "Station request từ ngày 1 đến ngày này được tính vào Kỳ 1. (Mặc định: 15)",
	},
	{
		key: "cycle2EmailDay",
		label: "Ngày gửi Email Mẫu 2 – Kỳ 2 (ngày của tháng sau)",
		description: "Gửi email tổng kết cho request Kỳ 2 vào ngày này của tháng tiếp theo. (Mặc định: 1)",
	},
	{
		key: "cycle1PayStart",
		label: "Ngày bắt đầu thanh toán Kỳ 1 (tháng sau)",
		description: "Khoảng thanh toán cho Kỳ 1 bắt đầu từ ngày này của tháng kế tiếp. (Mặc định: 1)",
	},
	{
		key: "cycle1PayEnd",
		label: "Ngày kết thúc thanh toán Kỳ 1 (tháng sau)",
		description: "Khoảng thanh toán cho Kỳ 1 kết thúc vào ngày này của tháng kế tiếp. (Mặc định: 5)",
	},
	{
		key: "cycle2PayStart",
		label: "Ngày bắt đầu thanh toán Kỳ 2 (tháng sau)",
		description: "Khoảng thanh toán cho Kỳ 2 bắt đầu từ ngày này của tháng kế tiếp. (Mặc định: 16)",
	},
	{
		key: "cycle2PayEnd",
		label: "Ngày kết thúc thanh toán Kỳ 2 (tháng sau)",
		description: "Khoảng thanh toán cho Kỳ 2 kết thúc vào ngày này của tháng kế tiếp. (Mặc định: 20)",
	},
];

export default function PaymentCycleSettings() {
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [config, setConfig] = useState<PaymentCycleConfig>({
		cycle1EndDay: 15,
		cycle2EmailDay: 1,
		cycle1PayStart: 1,
		cycle1PayEnd: 5,
		cycle2PayStart: 16,
		cycle2PayEnd: 20,
	});
	const [updatedAt, setUpdatedAt] = useState<string | undefined>();

	useEffect(() => {
		let mounted = true;
		(async () => {
			try {
				setLoading(true);
				const data = await getPaymentCycleConfig();
				if (!mounted) return;
				const { updatedAt: ua, ...rest } = data as any;
				setConfig(rest);
				setUpdatedAt(ua);
			} catch (err: any) {
				alert(`Không tải được cấu hình: ${err?.response?.data?.message || err?.message || "Vui lòng thử lại"}`);
			} finally {
				setLoading(false);
			}
		})();
		return () => {
			mounted = false;
		};
	}, []);

	const onChange = (key: keyof PaymentCycleConfig, value: string) => {
		const n = parseInt(value, 10);
		setConfig((prev) => ({ ...prev, [key]: Number.isFinite(n) ? n : 0 }));
	};

	const onSave = async () => {
		try {
			setSaving(true);
			await savePaymentCycleConfig(config);
			setUpdatedAt(new Date().toISOString());
			alert("Đã lưu cấu hình thanh toán thành công.");
		} catch (err: any) {
			alert(`Lưu thất bại: ${err?.response?.data?.message || err?.message || "Vui lòng thử lại"}`);
		} finally {
			setSaving(false);
		}
	};

	return (
		<MyBookingWrapper>
			<BookingContentWrap>
				<BookingContentName>Cấu hình Payment Cycle</BookingContentName>

				<div style={{ marginBottom: 16, color: "#6B7280", fontSize: 13 }}>
					Thiết lập các mốc ngày cho 2 kỳ thanh toán hàng tháng.
					{updatedAt && (
						<span style={{ marginLeft: 12 }}>
							Cập nhật lần cuối: <b>{new Date(updatedAt).toLocaleString("vi-VN")}</b>
						</span>
					)}
				</div>

				{/* Visual summary */}
				<div
					style={{
						background: "#F9FAFB",
						border: "1px solid #E5E7EB",
						borderRadius: 8,
						padding: "12px 16px",
						marginBottom: 20,
						fontSize: 13,
						lineHeight: 1.7,
					}}>
					<b>Tóm tắt cấu hình hiện tại:</b>
					<br />
					🗓️ <b>Kỳ 1:</b> Request ngày 1 → {config.cycle1EndDay} | Email Mẫu 2 ngày <b>16</b> cùng tháng |
					Thanh toán ngày{" "}
					<b>
						{config.cycle1PayStart}–{config.cycle1PayEnd}
					</b>{" "}
					tháng sau
					<br />
					🗓️ <b>Kỳ 2:</b> Request ngày {config.cycle1EndDay + 1} → cuối tháng | Email Mẫu 2 ngày{" "}
					<b>{config.cycle2EmailDay}</b> tháng sau | Thanh toán ngày{" "}
					<b>
						{config.cycle2PayStart}–{config.cycle2PayEnd}
					</b>{" "}
					tháng sau
				</div>

				{loading ? (
					<div style={{ textAlign: "center", padding: 32 }}>
						<CircularProgress />
					</div>
				) : (
					<div style={{ display: "grid", gap: 20, maxWidth: 560 }}>
						{FIELDS.map(({ key, label, description }) => (
							<div key={key}>
								<div style={{ fontWeight: 500, marginBottom: 4, fontSize: 14 }}>{label}</div>
								<div style={{ color: "#6B7280", fontSize: 12, marginBottom: 6 }}>{description}</div>
								<TextField
									type="number"
									value={config[key] ?? ""}
									onChange={(e) => onChange(key, e.target.value)}
									variant="outlined"
									size="small"
									style={{ width: 120 }}
									inputProps={{ min: 1, max: 31 }}
									disabled={saving}
								/>
							</div>
						))}
					</div>
				)}

				<ButtonUserWrap style={{ marginTop: 24 }}>
					<PermissionGate permission="settings.manage">
						<AdminTransactionAddButtton
							onClick={onSave}
							disabled={loading || saving}>
							{saving ? "Đang lưu..." : "Lưu cấu hình"}
						</AdminTransactionAddButtton>
					</PermissionGate>
				</ButtonUserWrap>
			</BookingContentWrap>
		</MyBookingWrapper>
	);
}
